export interface JpegCompressionOptions {
  maxBytes: number;
  qualitySteps: number[];
  fillColor?: string;
}

export async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

export async function loadImageBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file);
  }

  return new Promise((resolve, reject) => {
    const image = new Image();
    const url = URL.createObjectURL(file);
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = (event) => {
      URL.revokeObjectURL(url);
      reject(event);
    };
    image.src = url;
  });
}

export async function createJpegBlob(
  source: ImageBitmap | HTMLImageElement,
  quality: number,
  fillColor?: string
): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = source.width;
  canvas.height = source.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas context unavailable');
  }
  if (fillColor) {
    ctx.fillStyle = fillColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  ctx.drawImage(source as CanvasImageSource, 0, 0);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('JPEG conversion failed'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      quality
    );
  });
}

export async function compressImageToJpeg(
  file: File,
  options: JpegCompressionOptions
): Promise<{ dataUrl: string; byteSize: number }> {
  const bitmap = await loadImageBitmap(file);
  try {
    for (const quality of options.qualitySteps) {
      const blob = await createJpegBlob(bitmap, quality, options.fillColor);
      if (blob.size <= options.maxBytes) {
        const dataUrl = await blobToDataUrl(blob);
        return { dataUrl, byteSize: blob.size };
      }
    }

    throw new Error('Immagine troppo grande anche dopo la compressione.');
  } finally {
    if (typeof (bitmap as ImageBitmap).close === 'function') {
      (bitmap as ImageBitmap).close();
    }
  }
}
